import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import { ROUTES } from 'navigation/constants';
import colors from 'theme/emotion/colors';
import { ONBOARDING_VALIDATION, ONBOARDING_STEPS } from '../../constants';
import { setUserData, setRegistrationStep } from '../../slice';
import { getUserData } from '../../selectors';
import { submitRegistration } from '../../actions';
import {
  OnboardingBackButton,
  OnboardingActionButton,
  OnboardingButtonSection,
  OnboardingContainer,
  OnboardingScrollContainer,
  OnboardingContentSection,
  OnboardingTitleSection,
  OnboardingTitle,
  OnboardingDescription,
} from '../shared';

const InputSection = styled('div')({
  width: '100%',
  maxWidth: '28rem',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.neutral.white,
    borderRadius: '0.75rem',
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.25)',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary.light,
      borderWidth: '2px',
    },
    '&.Mui-error fieldset': {
      borderColor: colors.error.main,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: colors.primary.light,
    },
    '&.Mui-error': {
      color: colors.error.main,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '0.875rem',
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    fontFamily: 'Poppins, sans-serif',
    '&.Mui-error': {
      color: colors.error.main,
    },
  },
});

interface EmailEntryProps {}

const EmailEntry: React.FC<EmailEntryProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector(getUserData);
  const [email, setEmail] = useState(userData.email || '');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const isValidEmail = ONBOARDING_VALIDATION.EMAIL_REGEX.test(email);
  const showError = touched && email.length > 0 && !isValidEmail;

  // Auto-focus input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle keyboard visibility on mobile
  useEffect(() => {
    const handleFocus = () => {
      // Scroll input into view when keyboard opens
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      return () => {
        inputElement.removeEventListener('focus', handleFocus);
      };
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const handleContinue = () => {
    if (isValidEmail) {
      dispatch(setUserData({ email: email.trim() }));
      dispatch(setRegistrationStep(ONBOARDING_STEPS.CREATING_ACCOUNT as any));
      
      // Submit the registration with all collected data
      const formData = {
        ...userData,
        email: email.trim(),
      };
      dispatch(submitRegistration(formData));
      
      navigate(ROUTES.ONBOARDING.CREATING_ACCOUNT.path);
    }
  };

  const handleBack = () => {
    navigate(ROUTES.ONBOARDING.ROLE.path);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isValidEmail) {
      handleContinue();
    }
  };

  return (
    <OnboardingContainer>
      <OnboardingBackButton onClick={handleBack} />
      
      <OnboardingScrollContainer>
        <OnboardingContentSection>
          <OnboardingTitleSection>
            <OnboardingTitle>{t('onboarding.enterEmail')}</OnboardingTitle>
            <OnboardingDescription>
              {t('onboarding.emailDescription')}
            </OnboardingDescription>
          </OnboardingTitleSection>

          <InputSection>
            <StyledTextField
              ref={inputRef}
              label={t('onboarding.email')}
              type="email"
              value={email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              variant="outlined"
              autoComplete="email"
              error={showError}
              helperText={showError ? t('onboarding.invalidEmailError') : ''}
              inputProps={{
                inputMode: 'email',
              }}
            />
          </InputSection>
        </OnboardingContentSection>
      </OnboardingScrollContainer>

      <OnboardingButtonSection>
        <OnboardingActionButton
          disabled={!isValidEmail}
          onClick={handleContinue}
        >
          {t('onboarding.continue')}
        </OnboardingActionButton>
      </OnboardingButtonSection>
    </OnboardingContainer>
  );
};

export default EmailEntry; 